import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TuneIcon from '@material-ui/icons/Tune';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ExtensionIcon from '@material-ui/icons/Extension';
import BusinessIcon from '@material-ui/icons/Business';
import PublicIcon from '@material-ui/icons/Public';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import DashboardIcon from '@material-ui/icons/Dashboard';
import AssignmentIcon from '@material-ui/icons/Assignment';
import MapIcon from '@material-ui/icons/Map';
import SettingsIcon from '@material-ui/icons/Settings';
import AssessmentIcon from '@material-ui/icons/Assessment';
import CheckIcon from '@material-ui/icons/Check';
import { COLORS } from '../../theme';
import AdminHint from '../components/AdminHint';
import AdminViewToggle, { useAdminView } from '../components/AdminViewToggle';
import { UI_VARIANTS, uiVariantMeta } from '../../utils/manageHelpers';
import { CURRENCIES } from '../../utils/currencies';
import { COUNTRIES, TIMEZONES, LANGUAGES, ORDER_TYPES_META, WEEKDAYS } from '../../utils/localeOptions';

const API_BASE = '/planner-api';
// Rótulos PT — as CHAVES guardadas na BD continuam em inglês (delivery/…),
// só a apresentação é traduzida.
const SUBTYPE_LABELS = {
  cisterna: 'Cisterna', frigorifico: 'Frigorífico', light_truck: 'Camião ligeiro',
  general_cargo: 'Carga geral', car: 'Ligeiro / Carro',
};

// Secções do diálogo — nav à esquerda, detalhe à direita (espelha o ecrã de
// Preferências do planner, mas com a UI do backoffice e MAIS secções: o
// backoffice tem tudo). As tenantOnly não aparecem em perfis "add-on".
// Ícones dos módulos-pai = os MESMOS do menu principal do planner, para o
// operador reconhecer de imediato o botão que está a desligar.
const MENU_ICONS = {
  dashboard: DashboardIcon,
  route_planner: MapIcon,
  fleet: LocalShippingIcon,
  orders: AssignmentIcon,
  operations: SettingsIcon,
  reports: AssessmentIcon,
};

const P_SECTIONS = [
  { key: 'profile', label: 'Perfil', icon: BusinessIcon, tenantOnly: false },
  { key: 'general', label: 'Geral', icon: PublicIcon, tenantOnly: true },
  { key: 'modules', label: 'Módulos', icon: ExtensionIcon, tenantOnly: false },
  { key: 'experience', label: 'Experiência', icon: DashboardIcon, tenantOnly: true },
  { key: 'orders', label: 'Ordens', icon: AssignmentIcon, tenantOnly: true },
  { key: 'routes', label: 'Rotas', icon: LocalShippingIcon, tenantOnly: true },
];
const SUBTYPES = ['cisterna', 'frigorifico', 'light_truck', 'general_cargo', 'car'];

const EMPTY = {
  name: '', scope: 'tenant', isActive: true, notes: '',
  allowedOrderTypes: ['delivery', 'assistance'],
  uiVariant: 'generic', uiTheme: 'classic',
  defaultCurrency: 'EUR', defaultCountry: 'PT', defaultLanguage: 'pt', timezone: 'Europe/Lisbon',
  useTachograph: true, defaultVehicleSubType: 'cisterna',
  carRoutingProvider: 'here', truckRoutingProvider: 'here', routingProviderBySubType: {}, fuelPricesByCountry: {}, liveTrackingSource: 'both',
  avoidTolls: false, avoidFerries: false, avoidMotorways: false, avoidTunnels: false, avoidLEZ: false, defaultFuelType: 'diesel', singleCountry: false, trafficMonitor: false, geofenceFavorites: false,
  fleetQuickPanel: true, routesBrowser: true,
  defaultConsumption: '', defaultFuelPrice: '', defaultSpeed: '',
  truckWeight: '', truckAxleLoad: '', truckHeight: '', truckWidth: '', truckLength: '',
  orderFlows: { assistance: { usesApp: true }, delivery: { usesApp: true }, fuel: { usesApp: true } },
  workHoursPerDay: 8, workDays: [1, 2, 3, 4, 5],
  moduleIds: [],
};

const useStyles = makeStyles(() => ({
  header: { marginBottom: 22, display: 'flex', alignItems: 'center', gap: 14, background: 'var(--adm-header-bg, #e2f1e9)', border: '1px solid var(--adm-header-border, #bfe0d1)', borderRadius: 14, padding: '18px 22px' },
  titleIcon: { width: 44, height: 44, borderRadius: 12, background: 'var(--adm-brand-soft, rgba(47,93,80,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: '1.72rem', fontWeight: 700, color: COLORS.text, letterSpacing: '-0.02em' },
  subtitle: { fontSize: '0.78rem', color: COLORS.textSecondary, marginTop: 2 },
  newBtn: { marginLeft: 'auto', textTransform: 'none', background: 'var(--adm-brand, #2f5d50)', color: '#fff', borderRadius: 9, padding: '8px 18px', fontWeight: 600, boxShadow: '0 2px 8px rgba(47,93,80,0.3)' },
  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18 },
  tableCard: { background: 'var(--adm-card, #fff)', borderRadius: 16, border: '1px solid var(--adm-card-border, rgba(0,0,0,0.05))', boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)', overflow: 'visible' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '14px 20px', fontSize: '0.66rem', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.06))', background: 'var(--adm-thead-bg, #f8fafc)', position: 'sticky', top: 0, zIndex: 2 },
  td: { padding: '14px 20px', fontSize: '0.84rem', color: COLORS.text, borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.03))' },
  tableRow: { transition: 'background 0.15s', '&:hover': { background: 'var(--adm-hover, #f8fafc)' }, '&:last-child td': { borderBottom: 'none' } },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
  card: { background: 'var(--adm-card, #fff)', borderRadius: 14, border: '1px solid var(--adm-card-border, rgba(0,0,0,0.06))', boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)', padding: '18px 20px', position: 'relative' },
  cardTop: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap', paddingRight: 66 },
  pName: { fontSize: '1rem', fontWeight: 700, color: COLORS.text },
  scopeBadge: { fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '3px 9px', borderRadius: 999 },
  orgLine: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, background: 'var(--adm-section-bg, #f3f8f5)', border: '1px solid var(--adm-section-border, #e2efe8)', borderRadius: 8, padding: '6px 10px' },
  orgLineTxt: { fontSize: '0.78rem', fontWeight: 700, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  actionsCell: { display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },
  metaRow: { display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' },
  metaItem: { fontSize: '0.72rem', color: COLORS.textSecondary },
  metaVal: { fontWeight: 700, color: COLORS.text },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 12 },
  chip: { fontSize: '0.62rem', fontWeight: 600, color: 'var(--adm-text-dim, #475569)', background: 'var(--adm-hover, #f1f5f9)', padding: '3px 8px', borderRadius: 6 },
  cardActions: { position: 'absolute', top: 14, right: 14, display: 'flex', gap: 4 },
  iconBtn: { width: 28, height: 28, background: 'rgba(47,93,80,0.06)', borderRadius: 8, '&:hover': { background: 'rgba(47,93,80,0.14)' } },
  delBtn: { width: 28, height: 28, background: 'rgba(239,68,68,0.06)', borderRadius: 8, '&:hover': { background: 'rgba(239,68,68,0.14)' } },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  dialog: { background: 'var(--adm-card, #fff)', borderRadius: 18, width: 940, maxWidth: '96vw', maxHeight: '94vh', boxShadow: '0 24px 70px rgba(0,0,0,0.32)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  dialogHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '22px 30px 16px', borderBottom: '1px solid var(--adm-border, #eef2f7)' },
  dialogTitle: { fontSize: '1.2rem', fontWeight: 700, color: COLORS.text, display: 'flex', alignItems: 'center', gap: 12 },
  dialogTitleIcon: { width: 40, height: 40, borderRadius: 11, background: 'var(--adm-brand-soft, rgba(47,93,80,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  dialogTitleSub: { fontSize: '0.72rem', color: COLORS.textSecondary, fontWeight: 500, marginTop: 2 },
  dialogBody: { padding: '20px 30px 24px', overflowY: 'auto' },
  // Split nav-esquerda / detalhe-direita (espelha as Preferências do planner)
  dialogSplit: { display: 'flex', flex: 1, minHeight: 0 },
  dialogNav: { width: 210, flexShrink: 0, borderRight: '1px solid var(--adm-border, #eef2f7)', background: 'var(--adm-hover, #fafcfb)', padding: '16px 12px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 },
  pNavItem: { display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', borderRadius: 10, cursor: 'pointer', color: 'var(--adm-text-dim, #5a6a7e)', fontSize: '0.86rem', fontWeight: 600, transition: 'all 0.15s ease', '&:hover': { background: 'var(--adm-brand-soft, #eef5f1)', color: 'var(--adm-brand, #2f5d50)' } },
  pNavItemActive: { background: 'var(--adm-brand, #2f5d50)', color: 'var(--adm-on-brand, #c3f53c)', boxShadow: '0 4px 12px rgba(47,93,80,0.25)', '&:hover': { background: 'var(--adm-brand-dark, #274c41)', color: 'var(--adm-on-brand, #c3f53c)' } },
  pNavCount: { fontSize: '0.64rem', fontWeight: 800, background: 'rgba(120,130,140,0.20)', borderRadius: 999, padding: '1px 8px', minWidth: 20, textAlign: 'center' },
  dialogContent: { flex: 1, minWidth: 0, overflowY: 'auto', padding: '20px 26px 24px' },
  // Cada secção é um painel verde-claro didático.
  section: { marginBottom: 18, background: 'var(--adm-section-bg, #f3f8f5)', border: '1px solid var(--adm-section-border, #e2efe8)', borderRadius: 14, padding: '18px 20px' },
  sectionHi: { background: 'var(--adm-brand-soft, #eef5f1)', border: '1px solid #d8e9e1' },
  sectionTitle: { fontSize: '0.72rem', fontWeight: 800, color: 'var(--adm-brand, #2f5d50)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 8 },
  sectionHint: { fontSize: '0.74rem', color: 'var(--adm-brand-mid, #5a8577)', fontWeight: 500, marginTop: 3, marginBottom: 14, lineHeight: 1.45 },
  row: { display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' },
  field: { flex: 1, minWidth: 150 },
  label: { fontSize: '0.62rem', fontWeight: 700, color: 'var(--adm-text-muted, #6b8a80)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, display: 'block' },
  input: { width: '100%', border: '1.5px solid var(--adm-input-border, #d9e6df)', borderRadius: 9, padding: '10px 13px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--adm-text, #1e293b)', outline: 'none', background: 'var(--adm-input-bg, #ffffff)', boxSizing: 'border-box' },
  checkGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  // Árvore de módulos (menu = módulo)
  // ── Árvore de módulos (menu = módulo) ──────────────────────────────
  modGroup: {
    background: 'var(--adm-card, #fff)', border: '1.5px solid var(--adm-card-border, #e2e8f0)', borderRadius: 12,
    overflow: 'hidden', transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
  },
  modGroupOn: { borderColor: 'var(--adm-header-border, #bfe0d1)', boxShadow: '0 1px 3px var(--adm-brand-soft, rgba(47,93,80,0.08))' },
  modHead: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
    cursor: 'pointer', userSelect: 'none', transition: 'background 0.15s ease',
    '&:hover': { background: 'var(--adm-hover, #f8fafc)' },
  },
  modHeadOn: { background: 'var(--adm-brand-soft, #eef5f1)', '&:hover': { background: '#e6f0ea' } },
  modBox: {
    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
    border: '2px solid var(--adm-input-border, #cbd5e1)', background: 'var(--adm-input-bg, #fff)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.15s ease',
  },
  modBoxOn: { background: 'var(--adm-brand, #2f5d50)', borderColor: 'var(--adm-brand, #2f5d50)', color: '#fff' },
  modIcon: {
    width: 32, height: 32, borderRadius: 9, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--adm-hover, #f1f5f9)', color: 'var(--adm-text-muted, #94a3b8)', transition: 'all 0.15s ease',
  },
  modIconOn: { background: 'var(--adm-brand-soft, rgba(47,93,80,0.12))', color: 'var(--adm-brand, #2f5d50)' },
  modName: { fontSize: '0.88rem', fontWeight: 800, color: COLORS.text, lineHeight: 1.2 },
  modNameOff: { color: 'var(--adm-text-muted, #94a3b8)' },
  modDesc: { fontSize: '0.68rem', color: COLORS.textMuted, marginTop: 2, lineHeight: 1.35 },
  modCount: {
    marginLeft: 'auto', flexShrink: 0, fontSize: '0.64rem', fontWeight: 800,
    padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap',
    background: 'var(--adm-hover, #f1f5f9)', color: 'var(--adm-text-muted, #94a3b8)', border: '1px solid var(--adm-border, #e2e8f0)',
  },
  modCountOn: { background: 'var(--adm-card, #fff)', color: 'var(--adm-brand, #2f5d50)', borderColor: 'var(--adm-header-border, #bfe0d1)' },
  modKids: {
    display: 'flex', flexWrap: 'wrap', gap: 6,
    padding: '11px 14px 13px 46px', background: 'var(--adm-hover, #fbfdfc)',
    borderTop: '1px solid var(--adm-border, #eef2f7)', position: 'relative',
    transition: 'opacity 0.18s ease',
  },
  // Linha vertical da árvore, a alinhar com o ícone do pai
  modKidsRail: {
    position: 'absolute', left: 28, top: 0, bottom: 12, width: 2,
    background: 'var(--adm-border, #dbe7e1)', borderRadius: 2,
  },
  modKid: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: '0.74rem', fontWeight: 600, color: 'var(--adm-text-dim, #475569)',
    background: 'var(--adm-card, #fff)', border: '1.5px solid var(--adm-border, #e2e8f0)', borderRadius: 999,
    padding: '5px 11px 5px 7px', cursor: 'pointer', userSelect: 'none',
    transition: 'all 0.15s ease',
    '&:hover': { borderColor: 'var(--adm-header-border, #bfe0d1)' },
  },
  modKidOn: { background: 'var(--adm-brand-soft, rgba(47,93,80,0.08))', borderColor: 'var(--adm-brand, #2f5d50)', color: 'var(--adm-brand, #2f5d50)' },
  modKidBox: {
    width: 14, height: 14, borderRadius: 4, flexShrink: 0,
    border: '2px solid var(--adm-input-border, #cbd5e1)', background: 'var(--adm-input-bg, #fff)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modKidBoxOn: { background: 'var(--adm-brand, #2f5d50)', borderColor: 'var(--adm-brand, #2f5d50)', color: '#fff' },
  modBar: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
    padding: '10px 12px', background: 'var(--adm-card, #fff)', border: '1px solid var(--adm-card-border, #d9e6df)', borderRadius: 10,
  },
  modBarBtn: {
    fontSize: '0.7rem', fontWeight: 700, textTransform: 'none', color: 'var(--adm-brand, #2f5d50)',
    borderRadius: 8, padding: '3px 12px', minWidth: 0,
    '&:hover': { background: 'var(--adm-brand-soft, #eef5f1)' },
  },
  check: { display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.8rem', fontWeight: 600, color: 'var(--adm-text, #334155)', background: 'var(--adm-input-bg, #ffffff)', border: '1.5px solid var(--adm-input-border, #d9e6df)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', userSelect: 'none' },
  checkOn: { background: 'var(--adm-brand-soft, rgba(47,93,80,0.10))', borderColor: 'var(--adm-brand, #2f5d50)', color: 'var(--adm-brand, #2f5d50)' },
  dialogFooter: { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '16px 30px', borderTop: '1px solid var(--adm-border, #e2e8f0)', background: 'var(--adm-hover, #f8fafc)' },
  cancelBtn: { fontSize: '0.8rem', textTransform: 'none', color: 'var(--adm-text-dim, #64748b)', borderColor: 'var(--adm-border, #e2e8f0)', borderRadius: 8, padding: '6px 16px' },
  saveBtn: { fontSize: '0.8rem', textTransform: 'none', background: 'var(--adm-brand, #2f5d50)', color: '#fff', borderRadius: 8, padding: '6px 20px', '&:disabled': { background: '#e2e8f0', color: '#94a3b8' } },
  err: { color: 'var(--adm-danger, #dc2626)', fontSize: '0.78rem', fontWeight: 600, alignSelf: 'center', marginRight: 'auto' },
}));

function AdminProfiles({ token, currentUser }) {
  const classes = useStyles();
  const auth = { Authorization: `Bearer ${token}` };
  const [profiles, setProfiles] = useState([]);
  const [view, setView] = useAdminView('profiles', 'cards');
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (form)
  const [pSection, setPSection] = useState('profile'); // secção ativa no diálogo
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newFuelCc, setNewFuelCc] = useState(''); // código ISO2 do país a adicionar aos preços

  // Tipos de OT visíveis no card "Ordens". Um admin de ÂMBITO RESTRITO
  // (managedClientIds) só vê o tipo "combustível" se algum dos seus perfis já o
  // usar — senão esconde-se (ex.: francisco_bk gere Petrotec+Casais = assistência
  // + mercadoria, não combustível). O admin global (sem âmbito) vê sempre os 3.
  const isScopedAdmin = Array.isArray(currentUser?.managedClientIds) && currentUser.managedClientIds.length > 0;
  const scopeHasFuel = profiles.some(p => (Array.isArray(p.allowedOrderTypes) && p.allowedOrderTypes.includes('fuel')) || (p.extraSettings?.uiVariant || p.uiVariant) === 'fuel');
  const visibleOrderTypes = ORDER_TYPES_META.filter(t => !(t.key === 'fuel' && isScopedAdmin && !scopeHasFuel));

  async function load() {
    try {
      const [pr, mo] = await Promise.all([
        fetch(`${API_BASE}/profiles`, { headers: auth }),
        fetch(`${API_BASE}/modules`, { headers: auth }),
      ]);
      if (pr.ok) setProfiles(await pr.json());
      if (mo.ok) setModules(await mo.json());
    } catch (e) { console.error('Profiles load error:', e); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function openNew() { setError(''); setPSection('profile'); setEditing({ ...EMPTY, orderFlows: { ...EMPTY.orderFlows }, allowedOrderTypes: [...EMPTY.allowedOrderTypes], moduleIds: [] }); }
  function openEdit(p) {
    setError('');
    setPSection('profile');
    setEditing({
      ...EMPTY, ...p,
      defaultConsumption: p.defaultConsumption ?? '', defaultFuelPrice: p.defaultFuelPrice ?? '', defaultSpeed: p.defaultSpeed ?? '',
      truckWeight: p.truckWeight ?? '', truckAxleLoad: p.truckAxleLoad ?? '', truckHeight: p.truckHeight ?? '', truckWidth: p.truckWidth ?? '', truckLength: p.truckLength ?? '',
      carRoutingProvider: p.carRoutingProvider || 'here', truckRoutingProvider: p.truckRoutingProvider || 'here', liveTrackingSource: p.liveTrackingSource || 'both',
      routingProviderBySubType: p.routingProviderBySubType || p.extraSettings?.routingProviderBySubType || {},
      fuelPricesByCountry: p.fuelPricesByCountry || p.extraSettings?.fuelPricesByCountry || {},
      singleCountry: !!(p.singleCountry ?? p.extraSettings?.singleCountry),
      avoidTunnels: !!(p.avoidTunnels ?? p.extraSettings?.avoidTunnels),
      avoidLEZ: !!(p.avoidLEZ ?? p.extraSettings?.avoidLEZ),
      defaultFuelType: (p.defaultFuelType ?? p.extraSettings?.defaultFuelType) || 'diesel',
      trafficMonitor: !!(p.trafficMonitor ?? p.extraSettings?.trafficMonitor),
      geofenceFavorites: !!(p.geofenceFavorites ?? p.extraSettings?.geofenceFavorites),
      fleetQuickPanel: (p.fleetQuickPanel ?? p.extraSettings?.fleetQuickPanel) !== false,
      routesBrowser: (p.routesBrowser ?? p.extraSettings?.routesBrowser) !== false,
      uiVariant: p.extraSettings?.uiVariant || p.uiVariant || 'generic',
      uiTheme: (p.extraSettings?.uiTheme ?? p.uiTheme) === 'modern' ? 'modern' : 'classic',
      workHoursPerDay: p.extraSettings?.workHoursPerDay ?? 8,
      workDays: Array.isArray(p.extraSettings?.workDays) ? p.extraSettings.workDays : [1, 2, 3, 4, 5],
      allowedOrderTypes: Array.isArray(p.allowedOrderTypes) ? p.allowedOrderTypes : ['delivery', 'assistance'],
      orderFlows: {
        assistance: { usesApp: p.orderFlows?.assistance?.usesApp !== false },
        delivery: { usesApp: p.orderFlows?.delivery?.usesApp !== false },
        fuel: { usesApp: p.orderFlows?.fuel?.usesApp !== false },
      },
      moduleIds: Array.isArray(p.moduleIds) ? p.moduleIds : [],
    });
  }

  // Duplicar perfil: pré-preenche o editor com uma cópia deste perfil, MAS
  // sem id (→ handleSave faz POST = novo perfil) e com nome "(cópia)". Abre o
  // editor para o utilizador rever/renomear e só depois gravar. O novo perfil
  // nasce SEM Organização associada (é ligado depois no ecrã de Organizações).
  function openDuplicate(p) {
    openEdit(p);
    setEditing(e => (e ? { ...e, id: undefined, name: `${p.name} (cópia)`, isActive: true } : e));
  }

  const set = (k, v) => setEditing(e => ({ ...e, [k]: v }));
  const toggleArr = (k, v) => setEditing(e => {
    const arr = new Set(e[k] || []);
    arr.has(v) ? arr.delete(v) : arr.add(v);
    return { ...e, [k]: [...arr] };
  });
  const setFlow = (t, v) => setEditing(e => ({ ...e, orderFlows: { ...e.orderFlows, [t]: { usesApp: v } } }));

  // Ligar/desligar um menu arrasta as peças que vivem dentro dele: ligar o pai
  // liga todos os filhos; desligar tira o pai e os filhos (o menu some, logo os
  // filhos ficariam inalcançáveis e a mentir no ecrã).
  const toggleParent = (parent, children) => setEditing(e => {
    const ids = new Set(e.moduleIds || []);
    const ligar = !ids.has(parent.id);
    [parent, ...children].forEach(m => (ligar ? ids.add(m.id) : ids.delete(m.id)));
    return { ...e, moduleIds: [...ids] };
  });

  // Atalhos da barra de resumo.
  const setAllModules = (on) => setEditing(e => ({ ...e, moduleIds: on ? modules.map(m => m.id) : [] }));

  const num = (v) => (v === '' || v == null ? null : Number(v));

  async function handleSave() {
    if (!editing.name.trim()) { setError('Nome obrigatório'); return; }
    setSaving(true); setError('');
    const f = editing;
    const isTenant = f.scope === 'tenant';
    const payload = {
      name: f.name.trim(), scope: f.scope, isActive: f.isActive, notes: f.notes || null,
      moduleIds: f.moduleIds,
    };
    if (isTenant) {
      Object.assign(payload, {
        allowedOrderTypes: f.allowedOrderTypes,
        uiVariant: f.uiVariant || 'generic',
        uiTheme: f.uiTheme === 'modern' ? 'modern' : 'classic',
        defaultCurrency: f.defaultCurrency, defaultCountry: (f.defaultCountry || 'PT').toUpperCase().slice(0, 2),
        defaultLanguage: f.defaultLanguage, timezone: f.timezone || 'Europe/Lisbon',
        useTachograph: f.useTachograph, defaultVehicleSubType: f.defaultVehicleSubType,
        carRoutingProvider: f.carRoutingProvider, truckRoutingProvider: f.truckRoutingProvider, liveTrackingSource: f.liveTrackingSource,
        routingProviderBySubType: f.routingProviderBySubType && typeof f.routingProviderBySubType === 'object' ? f.routingProviderBySubType : {},
        fuelPricesByCountry: f.fuelPricesByCountry && typeof f.fuelPricesByCountry === 'object' ? f.fuelPricesByCountry : {},
        singleCountry: !!f.singleCountry,
        trafficMonitor: !!f.trafficMonitor,
        geofenceFavorites: !!f.geofenceFavorites,
        fleetQuickPanel: !!f.fleetQuickPanel,
        routesBrowser: !!f.routesBrowser,
        avoidTolls: f.avoidTolls, avoidFerries: f.avoidFerries, avoidMotorways: f.avoidMotorways,
        avoidTunnels: !!f.avoidTunnels, avoidLEZ: !!f.avoidLEZ, defaultFuelType: f.defaultFuelType || 'diesel',
        defaultConsumption: num(f.defaultConsumption), defaultFuelPrice: num(f.defaultFuelPrice), defaultSpeed: num(f.defaultSpeed),
        truckWeight: num(f.truckWeight), truckAxleLoad: num(f.truckAxleLoad), truckHeight: num(f.truckHeight), truckWidth: num(f.truckWidth), truckLength: num(f.truckLength),
        orderFlows: f.orderFlows,
        // Horário de trabalho (base da "utilização útil" no dashboard)
        workHoursPerDay: Number(f.workHoursPerDay) > 0 ? Number(f.workHoursPerDay) : 8,
        workDays: Array.isArray(f.workDays) && f.workDays.length ? f.workDays : [1, 2, 3, 4, 5],
      });
    }
    try {
      const url = f.id ? `${API_BASE}/profiles/${f.id}` : `${API_BASE}/profiles`;
      const res = await fetch(url, { method: f.id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', ...auth }, body: JSON.stringify(payload) });
      if (res.ok) { setEditing(null); await load(); }
      else { const e = await res.json().catch(() => ({})); setError(e.error || 'Erro ao guardar'); }
    } catch (e) { setError('Erro de rede'); }
    setSaving(false);
  }

  async function handleDelete(p) {
    if (!window.confirm(`Apagar o perfil "${p.name}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/profiles/${p.id}`, { method: 'DELETE', headers: auth });
      if (res.ok) await load();
      else { const e = await res.json().catch(() => ({})); window.alert(e.error || 'Não foi possível apagar'); }
    } catch (e) { window.alert('Erro de rede'); }
  }

  if (loading) return <Box className={classes.loading}><CircularProgress size={36} style={{ color: 'var(--adm-brand, #2f5d50)' }} /></Box>;

  const moduleName = (id) => modules.find(m => m.id === id)?.name || modules.find(m => m.id === id)?.key || id;
  // Nome(s) da(s) Organização(ões) que usam o perfil.
  const orgNames = (p) => (p.orgs || []).map(o => o.name).join(', ');

  // Árvore "menu = módulo": pais (parentId null) com os seus filhos.
  // A API já devolve tudo ordenado por sortOrder.
  const moduleTree = modules
    .filter(m => !m.parentId)
    .map(parent => ({ parent, children: modules.filter(c => c.parentId === parent.id) }));
  const menusOn = editing ? moduleTree.filter(({ parent }) => editing.moduleIds.includes(parent.id)).length : 0;

  // Secções visíveis no diálogo (add-on só tem Perfil + Módulos) e a ativa.
  const navSections = editing ? P_SECTIONS.filter(s => editing.scope === 'tenant' || !s.tenantOnly) : [];
  const activeP = navSections.some(s => s.key === pSection) ? pSection : 'profile';

  return (
    <Box>
      <Box className={classes.header}>
        <Box className={classes.titleIcon}><TuneIcon style={{ color: 'var(--adm-brand, #2f5d50)', fontSize: 22 }} /></Box>
        <Box>
          <Typography className={classes.title}>Perfis</Typography>
          <Typography className={classes.subtitle}>{profiles.length} perfis · preferências, módulos e fluxo das OT reutilizáveis</Typography>
        </Box>
        <Button className={classes.newBtn} startIcon={<AddIcon />} onClick={openNew}>Novo perfil</Button>
      </Box>

      <AdminHint
        nodes={[{ text: 'Perfil', tone: 'indigo' }, { text: 'Organizações', tone: 'green' }, { text: 'Utilizadores', tone: 'teal' }]}
        labels={['tenant: aplica-se a', 'add-on: dá módulos extra a']}
        note="O Perfil é a configuração da Organização: preferências, módulos, fluxo das OT e variante de UI. Cada Organização tem o seu próprio Perfil — e é o mesmo sítio que as Preferências dentro do planner alteram (num Perfil, no outro; estão em sincronia). Os do tipo Add-on de utilizador dão apenas módulos extra a uma pessoa."
      />

      <Box className={classes.toolbar}>
        <Typography style={{ fontSize: '0.78rem', color: 'var(--adm-brand-mid, #5a8577)', fontWeight: 600 }}>
          Cada Organização tem o seu Perfil — é o mesmo que as Preferências do planner alteram.
        </Typography>
        <AdminViewToggle view={view} onChange={setView} />
      </Box>

      {view === 'table' ? (
        <Box className={classes.tableCard}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>Perfil</th>
                <th className={classes.th}>Entidade / Organização</th>
                <th className={classes.th}>Tipo</th>
                <th className={classes.th}>Variante</th>
                <th className={classes.th}>Módulos</th>
                <th className={classes.th}>Utilizadores</th>
                <th className={classes.th}>Estado</th>
                <th className={classes.th} style={{ width: 92 }}></th>
              </tr>
            </thead>
            <tbody>
              {profiles.map(p => {
                const isAddon = p.scope === 'user_addon';
                const vm = uiVariantMeta(p.extraSettings?.uiVariant);
                return (
                  <tr key={p.id} className={classes.tableRow}>
                    <td className={classes.td}>
                      <Typography style={{ fontWeight: 700, fontSize: '0.87rem', color: COLORS.text }}>{p.name}</Typography>
                      {!isAddon && <Typography style={{ fontSize: '0.7rem', color: COLORS.textMuted }}>{p.defaultCurrency} · {p.defaultCountry}</Typography>}
                    </td>
                    <td className={classes.td}>
                      {isAddon ? <span style={{ color: COLORS.textMuted }}>—</span>
                        : orgNames(p)
                          ? <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: COLORS.text }}>{orgNames(p)}</Typography>
                          : <span style={{ fontSize: '0.76rem', fontWeight: 600, color: '#b45309' }}>⚠ sem Organização</span>}
                    </td>
                    <td className={classes.td}>
                      <span className={classes.scopeBadge} style={isAddon ? { background: 'rgba(139,92,246,0.12)', color: '#7c3aed' } : { background: 'rgba(34,197,94,0.12)', color: '#15803d' }}>
                        {isAddon ? 'Add-on de utilizador' : 'Organização'}
                      </span>
                    </td>
                    <td className={classes.td}>
                      {isAddon ? <span style={{ color: COLORS.textMuted }}>—</span> : (
                        <span className={classes.scopeBadge} style={{ background: vm.bg, color: vm.color, border: `1px solid ${vm.border}` }}>{vm.icon} {vm.short}</span>
                      )}
                    </td>
                    <td className={classes.td}>
                      <span style={{ fontWeight: 700, color: COLORS.text }}>{(p.moduleNames || []).length}</span>
                    </td>
                    <td className={classes.td}>
                      <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: COLORS.text }}>
                        {isAddon ? p.addonUserCount : p.userCount}
                        {!isAddon && p.addonUserCount > 0 && (
                          <span style={{ fontSize: '0.7rem', fontWeight: 500, color: COLORS.textMuted }}> · +{p.addonUserCount} add-on</span>
                        )}
                      </Typography>
                    </td>
                    <td className={classes.td}>
                      <span className={classes.scopeBadge} style={p.isActive
                        ? { background: 'rgba(34,197,94,0.10)', color: '#15803d' }
                        : { background: 'var(--adm-hover, #f1f5f9)', color: 'var(--adm-text-muted, #94a3b8)' }}>
                        {p.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className={classes.td}>
                      {/* Contentor flex — sem ele os IconButton (28px, num td
                          estreito) partiam para linhas separadas. */}
                      <Box className={classes.actionsCell}>
                        <IconButton className={classes.iconBtn} size="small" title="Editar" onClick={() => openEdit(p)}><EditIcon style={{ fontSize: 14, color: 'var(--adm-brand, #2f5d50)' }} /></IconButton>
                        <IconButton className={classes.iconBtn} size="small" title="Duplicar perfil" onClick={() => openDuplicate(p)}><FileCopyIcon style={{ fontSize: 14, color: '#2563eb' }} /></IconButton>
                        <IconButton className={classes.delBtn} size="small" title="Apagar" onClick={() => handleDelete(p)}><DeleteIcon style={{ fontSize: 14, color: 'var(--adm-danger, #ef4444)' }} /></IconButton>
                      </Box>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      ) : (
      <Box className={classes.grid}>
        {profiles.map(p => {
          const isAddon = p.scope === 'user_addon';
          return (
            <Box key={p.id} className={classes.card}>
              <Box className={classes.cardActions}>
                <IconButton className={classes.iconBtn} size="small" title="Editar" onClick={() => openEdit(p)}><EditIcon style={{ fontSize: 14, color: 'var(--adm-brand, #2f5d50)' }} /></IconButton>
                <IconButton className={classes.iconBtn} size="small" title="Duplicar perfil" onClick={() => openDuplicate(p)}><FileCopyIcon style={{ fontSize: 14, color: '#2563eb' }} /></IconButton>
                <IconButton className={classes.delBtn} size="small" title="Apagar" onClick={() => handleDelete(p)}><DeleteIcon style={{ fontSize: 14, color: 'var(--adm-danger, #ef4444)' }} /></IconButton>
              </Box>
              <Box className={classes.cardTop}>
                <Typography className={classes.pName}>{p.name}</Typography>
                <span className={classes.scopeBadge} style={isAddon ? { background: 'rgba(139,92,246,0.12)', color: '#7c3aed' } : { background: 'rgba(34,197,94,0.12)', color: '#15803d' }}>
                  {isAddon ? 'Add-on user' : 'Organização'}
                </span>
                {!isAddon && (() => { const vm = uiVariantMeta(p.extraSettings?.uiVariant); return (
                  <span className={classes.scopeBadge} style={{ background: vm.bg, color: vm.color, border: `1px solid ${vm.border}` }}>{vm.icon} {vm.short}</span>
                ); })()}
                {!p.isActive && <span className={classes.scopeBadge} style={{ background: 'var(--adm-hover, #f1f5f9)', color: 'var(--adm-text-muted, #94a3b8)' }}>Inativo</span>}
              </Box>
              {/* A que Organização pertence este perfil (cada Org tem o seu) */}
              {!isAddon && (
                <Box className={classes.orgLine}>
                  <BusinessIcon style={{ fontSize: 15, color: 'var(--adm-brand, #2f5d50)' }} />
                  <Typography className={classes.orgLineTxt}>
                    {orgNames(p) || <span style={{ color: '#b45309', fontWeight: 600 }}>⚠ sem Organização</span>}
                  </Typography>
                </Box>
              )}
              <Box className={classes.metaRow}>
                {/* Utilizadores REAIS (os das Organizações que usam este perfil).
                    Antes mostrava só os add-on → dava sempre 0 e enganava. */}
                <Typography className={classes.metaItem}>
                  {isAddon ? 'Add-on a' : 'Utilizadores'} <span className={classes.metaVal}>{isAddon ? p.addonUserCount : p.userCount}</span>
                </Typography>
                {!isAddon && p.addonUserCount > 0 && (
                  <Typography className={classes.metaItem}>Add-on <span className={classes.metaVal}>{p.addonUserCount}</span></Typography>
                )}
                {!isAddon && <Typography className={classes.metaItem}>{p.defaultCurrency} · {p.defaultCountry}</Typography>}
              </Box>
              {/* Nomes PT dos módulos (antes mostrava as keys cruas em inglês) */}
              <Box className={classes.chips}>
                {(p.moduleNames || []).slice(0, 6).map(n => <span key={n} className={classes.chip}>{n}</span>)}
                {(p.moduleNames || []).length > 6 && (
                  <span className={classes.chip} style={{ fontWeight: 800 }}>+{p.moduleNames.length - 6}</span>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      )}

      {editing && (
        <Box className={classes.overlay} onClick={() => !saving && setEditing(null)}>
          <Box className={classes.dialog} onClick={e => e.stopPropagation()}>
            <Box className={classes.dialogHeader}>
              <Box className={classes.dialogTitle}>
                <span className={classes.dialogTitleIcon}><TuneIcon style={{ color: 'var(--adm-brand, #2f5d50)', fontSize: 20 }} /></span>
                <Box>
                  <Typography style={{ fontSize: '1.2rem', fontWeight: 700, color: COLORS.text, lineHeight: 1.1 }}>{editing.id ? 'Editar perfil' : 'Novo perfil'}</Typography>
                  <Typography className={classes.dialogTitleSub}>A receita de configuração — escolhe os módulos e as preferências que as Organizações vão herdar.</Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setEditing(null)} style={{ color: 'var(--adm-text-muted, #94a3b8)' }}><CloseIcon style={{ fontSize: 18 }} /></IconButton>
            </Box>

            <Box className={classes.dialogSplit}>
             {/* NAV esquerda */}
             <Box className={classes.dialogNav}>
               {navSections.map(s => {
                 const Icon = s.icon;
                 const isOn = activeP === s.key;
                 return (
                   <Box key={s.key} className={`${classes.pNavItem} ${isOn ? classes.pNavItemActive : ''}`} onClick={() => setPSection(s.key)}>
                     <Icon style={{ fontSize: 18 }} />
                     <span style={{ flex: 1 }}>{s.label}</span>
                     {s.key === 'modules' && <span className={classes.pNavCount}>{editing.moduleIds.length}</span>}
                   </Box>
                 );
               })}
             </Box>

             {/* DETALHE direita */}
             <Box className={classes.dialogContent}>
              {activeP === 'profile' && (
              <Box className={classes.section} style={{ marginBottom: 0 }}>
                <Typography className={classes.sectionTitle}>Identidade</Typography>
                <Typography className={classes.sectionHint}>Como se chama este perfil e a que se aplica. "Organização" traz preferências + módulos + fluxo; "Add-on de utilizador" só dá módulos extra a uma pessoa.</Typography>
                <Box className={classes.row}>
                  <Box className={classes.field} style={{ flex: 2 }}>
                    <label className={classes.label}>Nome</label>
                    <input className={classes.input} value={editing.name} onChange={e => set('name', e.target.value)} autoFocus />
                  </Box>
                  <Box className={classes.field}>
                    <label className={classes.label}>Tipo de perfil</label>
                    <select className={classes.input} value={editing.scope} onChange={e => set('scope', e.target.value)}>
                      <option value="tenant">🏢 Perfil de Organização</option>
                      <option value="user_addon">➕ Add-on de utilizador</option>
                    </select>
                  </Box>
                </Box>
                {/* Explica a combo "Tipo" — muda conforme a escolha. */}
                <Box style={{ background: 'var(--adm-card, #fff)', border: '1px solid var(--adm-card-border, #d9e6df)', borderRadius: 10, padding: '10px 13px', marginBottom: 14 }}>
                  <Typography style={{ fontSize: '0.76rem', color: '#3f6b5c', lineHeight: 1.5 }}>
                    {editing.scope === 'tenant'
                      ? 'Perfil de Organização — é o normal, e é o que queres em 99% dos casos. Contém a configuração completa de uma empresa: preferências, módulos, fluxo das OT e variante de UI. Cada Organização tem o seu.'
                      : 'Add-on de utilizador — NÃO é a configuração de uma empresa. Serve só para dar módulos EXTRA a uma pessoa específica, por cima do que a Organização dela já dá. Atribui-se no ecrã Utilizadores, no campo "Perfil add-on".'}
                  </Typography>
                </Box>
                <Box className={classes.row} style={{ marginBottom: 0 }}>
                  <Box className={classes.field} style={{ flex: 2 }}>
                    <label className={classes.label}>Notas</label>
                    <input className={classes.input} value={editing.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Opcional" />
                  </Box>
                  <Box className={classes.field}>
                    <label className={classes.label}>Estado</label>
                    <Box className={`${classes.check} ${editing.isActive ? classes.checkOn : ''}`} onClick={() => set('isActive', !editing.isActive)}>
                      <input type="checkbox" checked={editing.isActive} readOnly /> Ativo
                    </Box>
                  </Box>
                </Box>
              </Box>
              )}

              {/* Módulos — as "peças" do planner */}
              {activeP === 'modules' && (
              <Box style={{ marginBottom: 0 }}>
                <Typography className={classes.sectionTitle} style={{ color: 'var(--adm-brand, #2f5d50)' }}>
                  <ExtensionIcon style={{ fontSize: 16 }} /> Módulos — as peças do planner
                </Typography>
                <Typography className={classes.sectionHint}>
                  Cada cartão é um botão do menu principal do planner; as peças por baixo vivem dentro
                  desse menu. Ligar ou desligar um menu arrasta as suas peças — e desligá-lo esconde-o
                  por completo ao cliente.
                </Typography>
                {/* Barra de resumo + atalhos */}
                <Box className={classes.modBar}>
                  <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--adm-brand, #2f5d50)' }}>
                    {menusOn} de {moduleTree.length} menus · {editing.moduleIds.length} peças
                  </Typography>
                  <Box style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                    <Button className={classes.modBarBtn} onClick={() => setAllModules(true)}>Ligar tudo</Button>
                    <Button className={classes.modBarBtn} onClick={() => setAllModules(false)}>Desligar tudo</Button>
                  </Box>
                </Box>

                <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {moduleTree.map(({ parent, children }) => {
                    const onParent = editing.moduleIds.includes(parent.id);
                    const onKids = children.filter(c => editing.moduleIds.includes(c.id)).length;
                    const Icon = MENU_ICONS[parent.key] || ExtensionIcon;
                    return (
                      <Box key={parent.id} className={`${classes.modGroup} ${onParent ? classes.modGroupOn : ''}`}>
                        {/* Cabeçalho = o botão do menu no planner */}
                        <Box
                          className={`${classes.modHead} ${onParent ? classes.modHeadOn : ''}`}
                          onClick={() => toggleParent(parent, children)}
                        >
                          <Box className={`${classes.modBox} ${onParent ? classes.modBoxOn : ''}`}>
                            {onParent && <CheckIcon style={{ fontSize: 13 }} />}
                          </Box>
                          <Box className={`${classes.modIcon} ${onParent ? classes.modIconOn : ''}`}>
                            <Icon style={{ fontSize: 18 }} />
                          </Box>
                          <Box style={{ minWidth: 0 }}>
                            <Typography className={`${classes.modName} ${onParent ? '' : classes.modNameOff}`}>
                              {parent.name || parent.key}
                            </Typography>
                            {parent.description && (
                              <Typography className={classes.modDesc}>{parent.description}</Typography>
                            )}
                          </Box>
                          {children.length > 0 && (
                            <span className={`${classes.modCount} ${onParent ? classes.modCountOn : ''}`}>
                              {onKids}/{children.length} peças
                            </span>
                          )}
                        </Box>

                        {/* Peças dentro do menu */}
                        {children.length > 0 && (
                          <Box className={classes.modKids} style={{ opacity: onParent ? 1 : 0.4 }}>
                            <Box className={classes.modKidsRail} />
                            {children.map(c => {
                              const on = editing.moduleIds.includes(c.id);
                              return (
                                <Box
                                  key={c.id}
                                  className={`${classes.modKid} ${on ? classes.modKidOn : ''}`}
                                  onClick={() => onParent && toggleArr('moduleIds', c.id)}
                                  title={onParent ? (c.description || '') : 'Liga primeiro o menu acima'}
                                  style={{ cursor: onParent ? 'pointer' : 'not-allowed' }}
                                >
                                  <Box className={`${classes.modKidBox} ${on ? classes.modKidBoxOn : ''}`}>
                                    {on && <CheckIcon style={{ fontSize: 10 }} />}
                                  </Box>
                                  {c.name || c.key}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              )}

              {/* Experiência / UI */}
              {activeP === 'experience' && (
                <Box className={classes.section} style={{ marginBottom: 0 }}>
                  <Typography className={classes.sectionTitle}>Experiência (ecrãs & dashboard)</Typography>
                  <Typography className={classes.sectionHint}>A variante decide o "aspeto" do planner para este cliente — que ecrãs, botões e dashboard aparecem.</Typography>
                  <Box className={classes.row} style={{ marginBottom: 0 }}>
                    <Box className={classes.field} style={{ flex: 2 }}>
                      <label className={classes.label}>Variante de UI</label>
                      <select className={classes.input} value={editing.uiVariant || 'generic'} onChange={e => set('uiVariant', e.target.value)}>
                        {Object.values(UI_VARIANTS).map(v => (
                          <option key={v.key} value={v.key}>{v.icon} {v.label}</option>
                        ))}
                      </select>
                    </Box>
                    <Box className={classes.field} style={{ flex: 3, display: 'flex', alignItems: 'flex-end' }}>
                      <Typography style={{ fontSize: '0.72rem', color: 'var(--adm-text-muted, #94a3b8)', lineHeight: 1.4 }}>
                        Determina os ecrãs, botões e o dashboard mostrados. É o "formato" do produto para este cliente — combustível, assistência técnica ou base.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Geral — Localização & moeda */}
              {activeP === 'general' && (
                <Box className={classes.section} style={{ marginBottom: 0 }}>
                  <Typography className={classes.sectionTitle}>Localização & moeda</Typography>
                  <Typography className={classes.sectionHint}>Valores por omissão do cliente: moeda dos custos, país, idioma e fuso horário.</Typography>
                  <Box className={classes.row} style={{ marginBottom: 0 }}>
                    <Box className={classes.field}><label className={classes.label}>Moeda por defeito</label>
                      <select className={classes.input} value={editing.defaultCurrency || 'EUR'} onChange={e => set('defaultCurrency', e.target.value)}>
                        {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.label} ({c.symbol})</option>)}
                      </select></Box>
                    <Box className={classes.field}><label className={classes.label}>País por defeito</label>
                      <select className={classes.input} value={editing.defaultCountry || 'PT'} onChange={e => set('defaultCountry', e.target.value)}>
                        {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select></Box>
                    <Box className={classes.field}><label className={classes.label}>Fuso horário</label>
                      <select className={classes.input} value={editing.timezone || 'Europe/Lisbon'} onChange={e => set('timezone', e.target.value)}>
                        {TIMEZONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select></Box>
                    <Box className={classes.field}><label className={classes.label}>Idioma por defeito</label>
                      <select className={classes.input} value={editing.defaultLanguage || 'pt'} onChange={e => set('defaultLanguage', e.target.value)}>
                        {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                      </select></Box>
                  </Box>

                  {/* Horário de trabalho — base do indicador "utilização útil" */}
                  <Box style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #d9e6df' }}>
                    <Typography className={classes.sectionTitle} style={{ fontSize: '0.68rem' }}>Horário de trabalho</Typography>
                    <Typography className={classes.sectionHint}>
                      Quando a frota PODE trabalhar. É a base da "utilização útil" no dashboard — sem isto, a utilização seria medida contra 24h/dia (noites e fins-de-semana incluídos) e daria sempre perto de 0%. Os feriados do país escolhido acima são excluídos automaticamente.
                    </Typography>
                    <Box className={classes.row} style={{ marginBottom: 10 }}>
                      <Box className={classes.field} style={{ maxWidth: 190 }}>
                        <label className={classes.label}>Horas por dia</label>
                        <input className={classes.input} type="number" min="1" max="24" step="0.5"
                          value={editing.workHoursPerDay ?? 8}
                          onChange={e => set('workHoursPerDay', e.target.value === '' ? '' : Number(e.target.value))} />
                      </Box>
                    </Box>
                    <label className={classes.label}>Dias de trabalho</label>
                    <Box className={classes.checkGrid}>
                      {WEEKDAYS.map(d => {
                        const on = (editing.workDays || [1, 2, 3, 4, 5]).includes(d.value);
                        return (
                          <Box key={d.value} className={`${classes.check} ${on ? classes.checkOn : ''}`}
                            onClick={() => toggleArr('workDays', d.value)}>
                            <input type="checkbox" checked={on} readOnly /> {d.label}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Ordens — tipos permitidos + fluxo (usa App) */}
              {activeP === 'orders' && (
                <Box className={classes.section} style={{ marginBottom: 0 }}>
                  <Typography className={classes.sectionTitle}>Ordens de serviço</Typography>
                  <Typography className={classes.sectionHint}>Os três tipos de OT. Liga à esquerda os que esta Organização cria; à direita define, por tipo, se o técnico usa a App no fluxo.</Typography>
                  <Box style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', alignItems: 'center', fontSize: '0.6rem', fontWeight: 700, color: 'var(--adm-text-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                    <span>Tipo de OT (permitido?)</span><span>App do técnico</span>
                  </Box>
                  <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {visibleOrderTypes.map(t => {
                      const allowed = editing.allowedOrderTypes.includes(t.key);
                      const usesApp = editing.orderFlows[t.key]?.usesApp !== false;
                      return (
                        <Box key={t.key} style={{ display: 'grid', gridTemplateColumns: '1fr 170px', gap: 10 }}>
                          <Box className={`${classes.check} ${allowed ? classes.checkOn : ''}`} onClick={() => toggleArr('allowedOrderTypes', t.key)}>
                            <input type="checkbox" checked={allowed} readOnly /> {t.icon} {t.label}
                          </Box>
                          <Box
                            className={`${classes.check} ${allowed && usesApp ? classes.checkOn : ''}`}
                            onClick={() => allowed && setFlow(t.key, !usesApp)}
                            style={{ justifyContent: 'center', opacity: allowed ? 1 : 0.45, cursor: allowed ? 'pointer' : 'not-allowed' }}
                          >
                            📱 {allowed && usesApp ? 'Com App' : 'Sem App'}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Rotas & veículo */}
              {activeP === 'routes' && (
                <Box className={classes.section} style={{ marginBottom: 0 }}>
                  <Typography className={classes.sectionTitle}>Rotas & veículo</Typography>
                  <Typography className={classes.sectionHint}>Parâmetros por omissão do cálculo de rota: tipo de veículo, fornecedor, tracking, consumos e restrições.</Typography>
                  {/* Legenda dos tipos de veículo — ajuda o operador a enquadrar
                      (ex.: uma pickup é "Ligeiro / Carro", não "Camião ligeiro"). */}
                  <Box style={{ background: 'var(--adm-section-bg, #f5f9f7)', border: '1px solid var(--adm-section-border, #d9e6df)', borderRadius: 8, padding: '8px 11px', margin: '2px 0 12px' }}>
                    <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#2f5d4a', marginBottom: 4 }}>Como enquadrar o tipo de veículo</Typography>
                    <Typography style={{ fontSize: '0.7rem', color: '#4b6b5e', lineHeight: 1.5 }}>
                      <b>Ligeiro / Carro</b> — carros, carrinhas pequenas e <b>pickups</b>. Modo carro, sem restrições de camião (mais rápido).<br />
                      <b>Camião ligeiro</b> — comercial até ~7,5 t. · <b>Carga geral (Mercadorias)</b> — camião de mercadorias ~24 t.<br />
                      <b>Frigorífico</b> — ~26 t. · <b>Cisterna</b> — ~40 t. <span style={{ color: '#7a9488' }}>(os pesados usam routing de camião: peso/altura/eixos/portagens)</span>
                    </Typography>
                  </Box>
                  <Box className={classes.row}>
                    <Box className={classes.field}><label className={classes.label}>Subtipo veículo</label>
                      <select className={classes.input} value={editing.defaultVehicleSubType} onChange={e => set('defaultVehicleSubType', e.target.value)}>{SUBTYPES.map(s => <option key={s} value={s}>{SUBTYPE_LABELS[s] || s}</option>)}</select></Box>
                    <Box className={classes.field}><label className={classes.label}>Combustível</label>
                      <select className={classes.input} value={editing.defaultFuelType} onChange={e => set('defaultFuelType', e.target.value)}><option value="diesel">Gasóleo</option><option value="gasoline">Gasolina</option></select></Box>
                    <Box className={classes.field}><label className={classes.label}>Rota (ligeiros)</label>
                      <select className={classes.input} value={editing.carRoutingProvider} onChange={e => set('carRoutingProvider', e.target.value)}><option value="here">HERE</option><option value="google">Google</option><option value="hybrid">Híbrido</option></select></Box>
                    <Box className={classes.field}><label className={classes.label}>Rota (pesados)</label>
                      <select className={classes.input} value={editing.truckRoutingProvider} onChange={e => set('truckRoutingProvider', e.target.value)}><option value="here">HERE</option><option value="google">Google</option><option value="hybrid">Híbrido</option></select></Box>
                    <Box className={classes.field}><label className={classes.label}>Tracking ao vivo</label>
                      <select className={classes.input} value={editing.liveTrackingSource} onChange={e => set('liveTrackingSource', e.target.value)}><option value="both">Ambos</option><option value="app">App</option><option value="traccar">Traccar</option></select></Box>
                  </Box>
                  {/* Provider POR TIPO de veículo (override fino). Vazio = herda
                      dos baldes "Rota (ligeiros)" / "Rota (pesados)" acima. */}
                  <Box style={{ marginTop: 4 }}>
                    <Typography className={classes.sectionHint} style={{ marginBottom: 4 }}>Provider por tipo de veículo (opcional) — vazio herda de Rota (ligeiros)/(pesados).</Typography>
                    <Box className={classes.row} style={{ marginBottom: 0 }}>
                      {SUBTYPES.map(s => (
                        <Box key={s} className={classes.field}><label className={classes.label}>{SUBTYPE_LABELS[s] || s}</label>
                          <select className={classes.input}
                            value={(editing.routingProviderBySubType || {})[s] || ''}
                            onChange={e => {
                              const next = { ...(editing.routingProviderBySubType || {}) };
                              if (e.target.value) next[s] = e.target.value; else delete next[s];
                              set('routingProviderBySubType', next);
                            }}>
                            <option value="">— (herda)</option>
                            <option value="here">HERE</option>
                            <option value="google">Google</option>
                            <option value="hybrid">Híbrido</option>
                          </select></Box>
                      ))}
                    </Box>
                  </Box>
                  <Box className={classes.row} style={{ marginBottom: 0 }}>
                    <Box className={classes.field}><label className={classes.label}>Consumo (L/100)</label>
                      <input className={classes.input} type="number" value={editing.defaultConsumption} onChange={e => set('defaultConsumption', e.target.value)} /></Box>
                    <Box className={classes.field}><label className={classes.label}>Velocidade méd.</label>
                      <input className={classes.input} type="number" value={editing.defaultSpeed} onChange={e => set('defaultSpeed', e.target.value)} /></Box>
                    <Box className={classes.field} style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <Box className={`${classes.check} ${editing.useTachograph ? classes.checkOn : ''}`} onClick={() => set('useTachograph', !editing.useTachograph)} style={{ width: '100%' }}>
                        <input type="checkbox" checked={editing.useTachograph} readOnly /> Tacógrafo</Box>
                    </Box>
                  </Box>
                  {/* Aviso: o que cada campo faz e para que é usado. */}
                  <Box style={{ background: 'var(--adm-section-bg, #f5f9f7)', border: '1px solid var(--adm-section-border, #d9e6df)', borderRadius: 8, padding: '8px 11px', marginTop: 8 }}>
                    <Typography style={{ fontSize: '0.7rem', color: '#4b6b5e', lineHeight: 1.5 }}>
                      <b>Consumo (L/100)</b> — consumo médio por defeito; estima o custo de combustível da rota (litros = km ÷ 100 × consumo). É o valor inicial de uma rota nova.<br />
                      <b>Velocidade méd.</b> — só usada para estimar a duração de <b>pesados quando o provider é Google</b> (o Google só modela modo carro e dá tempos otimistas para camião). Para HERE e ligeiros não tem efeito. Default 60 km/h.<br />
                      <span style={{ color: '#7a9488' }}>O <b>preço do combustível</b> define-se em <b>Preferências → Combustível</b> (por país), não aqui.</span>
                    </Typography>
                  </Box>
                  <Box className={classes.checkGrid} style={{ marginTop: 12 }}>
                    <Box className={`${classes.check} ${editing.avoidTolls ? classes.checkOn : ''}`} onClick={() => set('avoidTolls', !editing.avoidTolls)}><input type="checkbox" checked={editing.avoidTolls} readOnly /> Evitar portagens</Box>
                    <Box className={`${classes.check} ${editing.avoidFerries ? classes.checkOn : ''}`} onClick={() => set('avoidFerries', !editing.avoidFerries)}><input type="checkbox" checked={editing.avoidFerries} readOnly /> Evitar ferries</Box>
                    <Box className={`${classes.check} ${editing.avoidMotorways ? classes.checkOn : ''}`} onClick={() => set('avoidMotorways', !editing.avoidMotorways)}><input type="checkbox" checked={editing.avoidMotorways} readOnly /> Evitar autoestradas</Box>
                    <Box className={`${classes.check} ${editing.avoidTunnels ? classes.checkOn : ''}`} onClick={() => set('avoidTunnels', !editing.avoidTunnels)}><input type="checkbox" checked={editing.avoidTunnels} readOnly /> Evitar túneis</Box>
                    <Box className={`${classes.check} ${editing.avoidLEZ ? classes.checkOn : ''}`} onClick={() => set('avoidLEZ', !editing.avoidLEZ)}><input type="checkbox" checked={editing.avoidLEZ} readOnly /> Evitar Zonas Baixas Emissões (LEZ)</Box>
                  </Box>
                  {/* Preços de combustível por país — espelha a tabela das
                      Preferências do planner (fuelPricesByCountry). Sobrepõe o
                      Boletim do Petróleo da UE. Vazio = automático. */}
                  <Box style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #d9e6df' }}>
                    <Typography className={classes.sectionTitle} style={{ fontSize: '0.82rem', marginBottom: 2 }}>Preços de combustível por país</Typography>
                    <Typography className={classes.sectionHint} style={{ marginBottom: 8 }}>
                      Preço por litro na <b>moeda local do país</b> (PT/ES em EUR, AO em AOA). Sobrepõe o Boletim do Petróleo da UE; vazio = automático. Espelha o que o operador vê em Preferências → Combustível.
                    </Typography>
                    {Object.keys(editing.fuelPricesByCountry || {}).length > 0 && (
                      <Box style={{ display: 'grid', gridTemplateColumns: '64px 1fr 1fr 34px', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                        <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--adm-text-dim, #64748b)' }}>PAÍS</Typography>
                        <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--adm-text-dim, #64748b)' }}>GASÓLEO</Typography>
                        <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--adm-text-dim, #64748b)' }}>GASOLINA</Typography>
                        <span />
                        {Object.keys(editing.fuelPricesByCountry).sort().map(cc => (
                          <React.Fragment key={cc}>
                            <Typography style={{ fontSize: '0.8rem', fontWeight: 700 }}>{cc}</Typography>
                            <input className={classes.input} type="number" step="0.01" min="0" value={editing.fuelPricesByCountry[cc]?.diesel ?? ''}
                              onChange={e => set('fuelPricesByCountry', { ...editing.fuelPricesByCountry, [cc]: { ...editing.fuelPricesByCountry[cc], diesel: e.target.value === '' ? undefined : Number(e.target.value) } })} />
                            <input className={classes.input} type="number" step="0.01" min="0" value={editing.fuelPricesByCountry[cc]?.gasoline ?? ''}
                              onChange={e => set('fuelPricesByCountry', { ...editing.fuelPricesByCountry, [cc]: { ...editing.fuelPricesByCountry[cc], gasoline: e.target.value === '' ? undefined : Number(e.target.value) } })} />
                            <Box onClick={() => { const next = { ...editing.fuelPricesByCountry }; delete next[cc]; set('fuelPricesByCountry', next); }} title="Remover país" style={{ cursor: 'pointer', color: 'var(--adm-danger, #ef4444)', textAlign: 'center' }}>🗑</Box>
                          </React.Fragment>
                        ))}
                      </Box>
                    )}
                    <Box style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input className={classes.input} style={{ width: 110, textTransform: 'uppercase' }} placeholder="País (ISO2)" maxLength={2}
                        value={newFuelCc} onChange={e => setNewFuelCc(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))} />
                      {(() => {
                        const canAdd = newFuelCc.length === 2 && !(editing.fuelPricesByCountry || {})[newFuelCc];
                        return (
                          <Box onClick={() => { if (!canAdd) return; set('fuelPricesByCountry', { ...(editing.fuelPricesByCountry || {}), [newFuelCc]: {} }); setNewFuelCc(''); }}
                            style={{ cursor: canAdd ? 'pointer' : 'default', padding: '7px 14px', borderRadius: 6, background: canAdd ? '#0f7a43' : '#cbd5e1', color: '#fff', fontSize: '0.74rem', fontWeight: 600, userSelect: 'none' }}>
                            + Adicionar país
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                  {/* Mono-país — otimização de velocidade do cálculo de rota. */}
                  <Box style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #d9e6df' }}>
                    <Box className={`${classes.check} ${editing.singleCountry ? classes.checkOn : ''}`} onClick={() => set('singleCountry', !editing.singleCountry)} style={{ width: '100%' }}>
                      <input type="checkbox" checked={!!editing.singleCountry} readOnly /> Perfil mono-país (rota mais rápida)
                    </Box>
                    <Typography style={{ fontSize: '0.72rem', color: 'var(--adm-brand-mid, #5a8577)', lineHeight: 1.45, marginTop: 6 }}>
                      Liga quando a operação é toda dentro do mesmo país (ex.: Angola). Com o provider Google, salta a chamada extra ao HERE que só serve para repartir a distância por país — o cálculo fica mais rápido. <b>Desliga</b> em perfis que fazem rotas entre vários países (ex.: Portugal ↔ Europa), senão o card "Combustível por País" fica sem a repartição correta.
                    </Typography>
                  </Box>

                  {/* Monitor de incidentes de trânsito (HERE Real-Time Traffic) — CUSTA e default OFF. */}
                  <Box style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #d9e6df' }}>
                    <Box className={`${classes.check} ${editing.trafficMonitor ? classes.checkOn : ''}`} onClick={() => set('trafficMonitor', !editing.trafficMonitor)} style={{ width: '100%' }}>
                      <input type="checkbox" checked={!!editing.trafficMonitor} readOnly /> Monitor de trânsito ao vivo (HERE)
                    </Box>
                    <Typography style={{ fontSize: '0.72rem', color: 'var(--adm-brand-mid, #5a8577)', lineHeight: 1.45, marginTop: 6 }}>
                      Avisa o operador de <b>acidentes/cortes ao vivo</b> nas rotas em curso, consultando o HERE a cada 5 min por rota em trânsito. <b>Custa</b> (HERE Real-Time Traffic) e só tem valor onde há trânsito ao vivo (Europa/EUA). <b>Em Angola não há cobertura</b> — deixa <b>desligado</b> (default). Liga só em perfis com cobertura.
                    </Typography>
                    {editing.trafficMonitor && (
                      <Box style={{ marginTop: 8, padding: '8px 10px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8 }}>
                        <Typography style={{ fontSize: '0.72rem', color: '#9a3412', lineHeight: 1.45 }}>
                          ⚠ <b>Só funciona com o provider HERE.</b> O monitor usa o HERE Real-Time Traffic. Em perfis que calculam rotas com <b>Google</b>, os incidentes <b>não entram no cálculo</b> — ligar isto <b>custa</b> (HERE Traffic) sem trazer valor. Confirma que as rotas deste perfil usam HERE antes de ligar.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Importar geofences/POIs do EcooGPS como favoritos do planner. */}
                  <Box style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #d9e6df' }}>
                    <Box className={`${classes.check} ${editing.geofenceFavorites ? classes.checkOn : ''}`} onClick={() => set('geofenceFavorites', !editing.geofenceFavorites)} style={{ width: '100%' }}>
                      <input type="checkbox" checked={!!editing.geofenceFavorites} readOnly /> Importar POIs/geofences do EcooGPS como favoritos
                    </Box>
                    <Typography style={{ fontSize: '0.72rem', color: 'var(--adm-brand-mid, #5a8577)', lineHeight: 1.45, marginTop: 6 }}>
                      Ao entrar, traz automaticamente as <b>geofences/POIs da conta EcooGPS do utilizador</b> para os <b>Locais guardados/Favoritos</b> do planner (1× por sessão, com dedup — não duplica). Fica também disponível um botão <b>"Importar geofences"</b> nos Favoritos. Ideal para não ter de recriar os pontos à mão.
                    </Typography>
                  </Box>

                  {/* Painéis flutuantes do mapa (Planeamento). Default LIGADO. */}
                  <Box style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #d9e6df' }}>
                    <Box className={`${classes.check} ${editing.fleetQuickPanel ? classes.checkOn : ''}`} onClick={() => set('fleetQuickPanel', !editing.fleetQuickPanel)} style={{ width: '100%' }}>
                      <input type="checkbox" checked={!!editing.fleetQuickPanel} readOnly /> Painel flutuante da Frota (mapa)
                    </Box>
                    <Box className={`${classes.check} ${editing.routesBrowser ? classes.checkOn : ''}`} onClick={() => set('routesBrowser', !editing.routesBrowser)} style={{ width: '100%', marginTop: 8 }}>
                      <input type="checkbox" checked={!!editing.routesBrowser} readOnly /> Painel flutuante de Rotas Gravadas (mapa)
                    </Box>
                    <Typography style={{ fontSize: '0.72rem', color: 'var(--adm-brand-mid, #5a8577)', lineHeight: 1.45, marginTop: 6 }}>
                      Mostra/esconde os painéis flutuantes no mapa do Planeamento (canto sup. esquerdo = Frota; fundo-centro = Rotas Gravadas). O painel <b>"Em trânsito"</b> é controlado pelo módulo <b>Monitor em trânsito</b> (secção Módulos).
                    </Typography>
                  </Box>
                </Box>
              )}
             </Box>
            </Box>

            <Box className={classes.dialogFooter}>
              {error && <Typography className={classes.err}>{error}</Typography>}
              <Button variant="outlined" className={classes.cancelBtn} onClick={() => setEditing(null)}>Cancelar</Button>
              <Button className={classes.saveBtn} onClick={handleSave} disabled={saving || !editing.name.trim()} startIcon={<SaveIcon style={{ fontSize: 16 }} />}>
                {saving ? 'A guardar...' : 'Guardar'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default AdminProfiles;
